import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ResourceRef } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '@components/confirm-dialog/confirm-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { WaterServicesService } from '@services/water-services.service';
import { WaterBasic } from '@services/waters.service';
import { firstValueFrom } from 'rxjs';
import { WaterSelectorDialogComponent } from '../water-services-dialog/water-selector-dialog.component';

interface WaterServiceGroup {
  waterId: number;
  waterTitle: string;
  updatedAt: Date;
}

@Component({
  selector: 'app-water-services-list',
  templateUrl: './water-services-list.component.html',
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTooltipModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaterServicesListComponent {
  private readonly waterServicesService = inject(WaterServicesService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  readonly displayedColumns: string[] = ['water', 'updatedAt', 'servicesCount', 'actions'];

  readonly waterServicesResource: ResourceRef<WaterServiceGroup[] | undefined> = rxResource({
    stream: () => this.waterServicesService.getAll().pipe(showGenericErrorToast(this.toastService, () => undefined)),
  });

  openCreateDialog() {
    const dialogRef = this.dialog.open<WaterSelectorDialogComponent, object, WaterBasic>(WaterSelectorDialogComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().subscribe((water: WaterBasic | undefined) => {
      if (water) {
        this.router.navigate(['/water-services', water.id]);
      }
    });
  }

  editWater(waterId: number) {
    this.router.navigate(['/water-services', waterId]);
  }

  deleteWater(waterId: number, waterTitle: string, event: MouseEvent) {
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('water-services.confirm.deletion-title'),
        prompt: this.translateService.instant('water-services.confirm.deletion-prompt', { waterTitle }),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deleteWaterConfirmed(waterId);
      }
    });
  }

  private async deleteWaterConfirmed(waterId: number): Promise<void> {
    try {
      await firstValueFrom(this.waterServicesService.deleteByWater(waterId));
      this.toastService.showSuccess(this.translateService.instant('water-services.toast.delete-success'));
      this.waterServicesResource.reload();
    } catch (error) {
      console.error('Error deleting water services:', error);
      this.toastService.showError(this.translateService.instant('water-services.toast.delete-failed'));
    }
  }
}
